import { InsurancePlan, Service } from '@dataTypes/insurance.types'

type CoverageRecord = {
  network: string
  network_type: number
  coverage_level: number
  amount: number
  remaining: number
}

const coverage = (
  network: string,
  networkType: number,
  coverageLevel: number,
  amount: number,
  remaining: number,
): CoverageRecord => ({
  network,
  network_type: networkType,
  coverage_level: coverageLevel,
  amount,
  remaining,
})

/** Builds plan services that drive the Insurance Summary network cards. */
const createDemoServices = (
  inNetworkName: string,
  outNetworkName = 'OUT OF NETWORK',
): Service[] => [
  {
    category: 'dental_care',
    benefits: [{ network_type: 1, co_insurance: 50, procedure_code: 'D2740' }],
    deductibles: [
      coverage(inNetworkName, 0, 1, 750, 0),
      coverage(inNetworkName, 0, 2, 750, 100),
      coverage(outNetworkName, 1, 1, 350, 0),
      coverage(outNetworkName, 1, 2, 350, 0),
    ],
    maximums: [
      coverage(inNetworkName, 0, 1, 4000, 1500),
      coverage(inNetworkName, 0, 2, 4000, 1500),
      coverage(outNetworkName, 1, 1, 4000, 125),
      coverage(outNetworkName, 1, 2, 4000, 125),
    ],
  },
  {
    category: 'implants',
    benefits: [],
    deductibles: [],
    maximums: [
      coverage(inNetworkName, 0, 1, 4000, 1500),
      coverage(outNetworkName, 1, 1, 4000, 1500),
    ],
  },
  {
    category: 'tmj',
    benefits: [],
    deductibles: [],
    maximums: [
      coverage(inNetworkName, 0, 1, 4000, 1500),
      coverage(outNetworkName, 1, 1, 4000, 1500),
    ],
  },
]

const createMockInsurancePlan = (
  overrides: Partial<InsurancePlan> &
    Pick<
      InsurancePlan,
      | 'insurance_plan_id'
      | 'carrier_name'
      | 'plan_name'
      | 'subscriber_fullname'
      | 'subscriber_member_id'
      | 'status'
    >,
): InsurancePlan => ({
  number: overrides.number ?? '1',
  payer_id: overrides.payer_id ?? 'PAYER-001',
  plan_type: overrides.plan_type ?? 'PPO',
  validity: overrides.validity ?? '05-09-2027',
  max_coverage: overrides.max_coverage ?? 4000,
  claim_histories: overrides.claim_histories ?? [],
  is_primary: overrides.is_primary ?? false,
  ...overrides,
})

export type MockInsuranceFamilyMember = {
  member_id: string
  first_name: string
  middle_name?: string
  last_name: string
  insurancePlans: InsurancePlan[]
}

export const mockInsuranceFamilyMembers: Record<
  string,
  MockInsuranceFamilyMember[]
> = {
  'mock-patient-1': [
    {
      member_id: 'mock-fam-patient-1-1',
      first_name: 'Cynthia',
      middle_name: 'Elizabeth',
      last_name: 'Doe',
      insurancePlans: [
        createMockInsurancePlan({
          insurance_plan_id: 'mock-ins-plan-fam-1-1',
          number: 'BC123456',
          carrier_name: 'Bluecross',
          payer_id: 'BCBS-001',
          plan_name: 'Work A+',
          plan_type: 'PPO',
          subscriber_fullname: 'John Mark Kennedy Doe',
          subscriber_member_id: 'BC123456789',
          status: 'Active',
          is_primary: true,
          validity: '05-09-2027',
          services: createDemoServices('Bluecross PPO'),
        }),
      ],
    },
  ],
}

export const mockInsurancePlans: Record<string, InsurancePlan[]> = {
  'mock-patient-1': [
    createMockInsurancePlan({
      insurance_plan_id: 'mock-ins-plan-1-1',
      number: 'BC123456',
      carrier_name: 'Bluecross',
      payer_id: 'BCBS-001',
      plan_name: 'Work A+',
      plan_type: 'PPO',
      subscriber_fullname: 'John Mark Kennedy Doe',
      subscriber_member_id: 'BC123456789',
      status: 'Active',
      is_primary: true,
      validity: '05-09-2027',
      services: createDemoServices('Bluecross PPO'),
    }),
    createMockInsurancePlan({
      insurance_plan_id: 'mock-ins-plan-1-2',
      number: 'SL998877',
      carrier_name: 'Sunlife',
      payer_id: 'SUNLIFE-004',
      plan_name: 'Health+',
      plan_type: 'PPO',
      subscriber_fullname: 'John Mark Kennedy Doe',
      subscriber_member_id: 'SL998877665',
      status: 'Active',
      is_primary: false,
      validity: '12-31-2027',
      services: createDemoServices('Sunlife PPO'),
    }),
  ],
  'mock-patient-2': [
    createMockInsurancePlan({
      insurance_plan_id: 'mock-ins-plan-2-1',
      number: 'AE789012',
      carrier_name: 'Bluecross',
      payer_id: 'BSCA-003',
      plan_name: 'Work A+',
      plan_type: 'PPO',
      subscriber_fullname: 'Jane Marie Johnson Smith',
      subscriber_member_id: 'AE789012345',
      status: 'Active',
      is_primary: true,
      validity: '05-09-2027',
      services: createDemoServices('Bluecross PPO'),
    }),
    createMockInsurancePlan({
      insurance_plan_id: 'mock-ins-plan-2-2',
      number: 'SL998877',
      carrier_name: 'Sunlife',
      payer_id: 'SUNLIFE-004',
      plan_name: 'Health+',
      plan_type: 'PPO',
      subscriber_fullname: 'Jane Marie Johnson Smith',
      subscriber_member_id: 'SL998877665',
      status: 'Active',
      is_primary: false,
      validity: '12-31-2027',
      services: createDemoServices('Sunlife PPO'),
    }),
  ],
}
